import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';

type TooltipState = {
  left: number;
  top: number;
} | null;

type MessageReactionChipProps = {
  messageId: string;
  count: number;
  reactedByMe: boolean;
  names: string[];
  onToggle: (messageId: string) => void;
  onRequestNames?: (messageId: string) => void;
  className?: string;
};

const getEmployeeInitials = (value: string): string => {
  const words = String(value || 'U').trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return 'U';
  return words.slice(0, 2).map((word) => word[0]?.toUpperCase() || '').join('') || 'U';
};

const isMobileViewport = (): boolean =>
  typeof window !== 'undefined' && window.innerWidth < 640;

const MessageReactionChip: React.FC<MessageReactionChipProps> = ({
  messageId,
  count,
  reactedByMe,
  names,
  onToggle,
  onRequestNames,
  className,
}) => {
  const [tooltip, setTooltip] = useState<TooltipState>(null);
  const [viewerOpen, setViewerOpen] = useState(false);
  const viewerCount = names.length || count;

  const requestNames = () => {
    onRequestNames?.(messageId);
  };

  const openViewer = () => {
    requestNames();
    setViewerOpen(true);
  };

  const showTooltip = (
    event: React.MouseEvent<HTMLButtonElement> | React.FocusEvent<HTMLButtonElement>
  ) => {
    if (isMobileViewport()) {
      return;
    }

    const rect = event.currentTarget.getBoundingClientRect();
    setTooltip({
      left: rect.left + rect.width / 2,
      top: rect.top - 10,
    });
    requestNames();
  };

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    openViewer();
  };

  const handleTouchEnd = (event: React.TouchEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    openViewer();
  };

  const defaultClassName = `group/reaction relative mt-1 flex min-h-[44px] min-w-[44px] items-center justify-center gap-1 rounded-full border px-3 py-2 text-[0px] ${
    reactedByMe
      ? 'border-blue-300 bg-blue-100 text-blue-700'
      : 'border-gray-200 bg-gray-100 text-gray-600'
  }`;

  return (
    <>
      <button
        type="button"
        onClick={handleClick}
        onTouchEnd={handleTouchEnd}
        onMouseEnter={showTooltip}
        onMouseLeave={() => setTooltip(null)}
        onFocus={showTooltip}
        onBlur={() => setTooltip(null)}
        className={className || defaultClassName}
      >
        <span className="text-xs">{'\u{1F44D}'}</span>
        <span>👍</span>
        <span className="text-xs">{count}</span>
      </button>

      {tooltip && typeof document !== 'undefined' && createPortal(
        <div
          className="pointer-events-none fixed z-[220] hidden max-w-[220px] -translate-x-1/2 -translate-y-full whitespace-normal break-words rounded-lg bg-slate-900 px-3 py-2 text-center text-[11px] font-semibold leading-snug text-white shadow-lg sm:block"
          style={{ left: tooltip.left, top: tooltip.top }}
          role="tooltip"
        >
          {names.join(', ') || 'Unknown User'}
          <span className="absolute left-1/2 top-full h-2 w-2 -translate-x-1/2 -translate-y-1/2 rotate-45 bg-slate-900" />
        </div>,
        document.body
      )}

      {viewerOpen && (
        <div className="fixed inset-0 z-[130] flex items-end bg-black/40 sm:hidden" onClick={() => setViewerOpen(false)}>
          <div
            className="max-h-[50vh] w-full overflow-y-auto rounded-t-3xl bg-white px-5 pb-[calc(env(safe-area-inset-bottom)+80px)] pt-3 shadow-2xl"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="mx-auto mb-4 h-1.5 w-12 rounded-full bg-slate-300" />
            <div className="mb-4 flex items-center justify-between gap-3">
              <h2 className="text-base font-bold text-slate-900">Reacted by ({viewerCount})</h2>
              <button
                type="button"
                onClick={() => setViewerOpen(false)}
                className="flex min-h-[44px] min-w-[44px] items-center justify-center rounded-full bg-slate-100 text-slate-600"
                aria-label="Close reactions"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="space-y-2">
              {names.length > 0 ? (
                names.map((name, index) => (
                  <div key={`${name}-${index}`} className="flex items-center gap-3 rounded-2xl bg-slate-50 px-4 py-3">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-slate-200 text-[11px] font-bold text-slate-700">
                      {getEmployeeInitials(name)}
                    </span>
                    <span className="min-w-0 flex-1 break-words text-sm font-semibold text-slate-800">{name}</span>
                  </div>
                ))
              ) : (
                <div className="flex items-center gap-3 rounded-2xl bg-slate-50 px-4 py-3">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-slate-200 text-[11px] font-bold text-slate-700">
                    U
                  </span>
                  <span className="text-sm font-semibold text-slate-500">Unknown User</span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default MessageReactionChip;
